import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search,
  Filter,
  CalendarDays,
  FileText,
  Download,
  RotateCcw,
  ChevronDown,
  X,
  Plus,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import { DateRange } from "react-day-picker";
import { endOfDay, startOfDay } from "date-fns";
import { useUserInfo } from "@/hooks/use-user-info";
import { isProgramAdmin } from "@/utils/role.utils";

interface TransactionFiltersProps {
  onFilterChange: (filters: any) => void;
  onExport: (format: "csv" | "csv-all-programs" | "pdf") => void;
  onDateRangeChange: (range: string) => void;
  onReset: () => void;
  merchants?: any[];
  searchType: string;
  setSearchType: (type: string) => void;
  searchTerm: string;
  onSearch: (term: string) => void;
  defaultMerchantCode?: string;
  lockMerchantFilter?: boolean;
  customDateRange?: DateRange;
  onCustomDateRangeChange?: (range: DateRange | undefined) => void;
}

const FILTER_CATEGORIES = [
  { label: "Date", value: "date", isArray: false },
  { label: "Merchant", value: "merchant", isArray: false },
  { label: "Status", value: "status", isArray: false }, // accrual status
  { label: "Order Status", value: "orderStatus", isArray: false }, // transaction status
];

// Accrual status filter options (existing logic)
const STATUS_OPTIONS = [
  { label: "Accrual Failed", value: "ACCRUAL_FAILED" },
  { label: "Accrual Successful", value: "ACCRUAL_SUCCESSFUL" },
  { label: "Not Accrued", value: "NOT_ACCRUED" },
];

// Order Status (transaction status) filter options: 0-Not Finalized, 1-Finalized, 2-Refunded
const ORDER_STATUS_OPTIONS = [
  { label: "Not Finalized", value: "0" },
  { label: "Finalized", value: "1" },
  { label: "Refunded", value: "2" },
];

export const TransactionFilters: React.FC<TransactionFiltersProps> = ({
  onFilterChange,
  onExport,
  onDateRangeChange,
  onReset,
  merchants = [],
  searchType,
  setSearchType,
  searchTerm,
  onSearch,
  defaultMerchantCode,
  lockMerchantFilter = false,
  customDateRange,
  onCustomDateRangeChange,
}) => {
  const { userInfo } = useUserInfo();
  const programAdmin = isProgramAdmin(userInfo?.roleCode);
  const [dateRange, setDateRange] = React.useState("");
  const [filterPopoverOpen, setFilterPopoverOpen] = React.useState(false);
  const [activeCategory, setActiveCategory] = React.useState("date");
  const [filterSelections, setFilterSelections] = React.useState<any>({
    status: "",
    merchant: defaultMerchantCode || "",
    orderStatus: "",
    dateRange: "none",
  });
  const [pendingFilterSelections, setPendingFilterSelections] =
    React.useState<any>({
      status: "",
      merchant: defaultMerchantCode || "",
      orderStatus: "",
      dateRange: "none",
    });
  const [searchInputs, setSearchInputs] = React.useState<any>({
    status: "",
    merchant: "",
  });

  const hasDateFilter =
    (filterSelections.dateRange && filterSelections.dateRange !== "none") ||
    (dateRange && dateRange !== "none" && dateRange !== "") ||
    Boolean(customDateRange?.from && customDateRange?.to);

  // Count of active filters
  const activeFilterCount =
    ["status", "merchant", "orderStatus"].reduce((acc, key) => {
      return acc + (filterSelections[key] ? 1 : 0);
    }, 0) + (hasDateFilter ? 1 : 0);

  React.useEffect(() => {
    // Sync pendingFilterSelections with filterSelections when popover opens
    if (filterPopoverOpen) {
      setPendingFilterSelections({ ...filterSelections });
      if (filterSelections.dateRange && filterSelections.dateRange !== "none") {
        setDateRange(filterSelections.dateRange);
      }
    }
    // eslint-disable-next-line
  }, [filterPopoverOpen]);

  React.useEffect(() => {
    if (defaultMerchantCode && !filterSelections.merchant) {
      const newFilterSelections = {
        ...filterSelections,
        merchant: defaultMerchantCode,
      };
      setFilterSelections(newFilterSelections);
      setPendingFilterSelections({
        ...pendingFilterSelections,
        merchant: defaultMerchantCode,
      });
      onFilterChange({ merchantCode: defaultMerchantCode });
    }
    // eslint-disable-next-line
  }, [defaultMerchantCode]);

  const getDateRangeMillis = (range: string) => {
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = null;
    switch (range) {
      case "today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );
        break;
      case "yesterday": {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = new Date(
          yesterday.getFullYear(),
          yesterday.getMonth(),
          yesterday.getDate()
        );
        end = new Date(
          yesterday.getFullYear(),
          yesterday.getMonth(),
          yesterday.getDate(),
          23,
          59,
          59,
          999
        );
        break;
      }
      case "7days":
        start = new Date(now);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "30days":
        start = new Date(now);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "thisMonth":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      default:
        start = null;
        end = null;
    }
    return {
      startDateInMillis: start ? start.getTime() : "",
      endDateInMillis: end ? end.getTime() : "",
    };
  };

  // Always emit API keys (incl. empty merchantCode) so parent merge can overwrite prior values
  const buildFilterPayload = (selections: any, activeDateRange: string) => {
    const merchantCode =
      lockMerchantFilter && defaultMerchantCode
        ? defaultMerchantCode
        : selections.merchant || "";

    const filterPayload: any = {
      status: selections.status || "",
      orderStatus: selections.orderStatus || "",
      merchantCode,
    };

    if (activeDateRange && activeDateRange !== "none") {
      const { startDateInMillis, endDateInMillis } =
        getDateRangeMillis(activeDateRange);
      filterPayload.startDateInMillis = startDateInMillis;
      filterPayload.endDateInMillis = endDateInMillis;
    } else if (customDateRange?.from && customDateRange?.to) {
      filterPayload.startDateInMillis = startOfDay(
        customDateRange.from
      ).getTime();
      filterPayload.endDateInMillis = endOfDay(customDateRange.to).getTime();
    } else {
      filterPayload.startDateInMillis = "";
      filterPayload.endDateInMillis = "";
    }

    return filterPayload;
  };

  const applyFilters = () => {
    const selections = {
      ...pendingFilterSelections,
      merchant:
        lockMerchantFilter && defaultMerchantCode
          ? defaultMerchantCode
          : pendingFilterSelections.merchant,
    };
    const appliedDateRange =
      dateRange && dateRange !== "none" ? dateRange : "none";
    setFilterSelections({
      ...selections,
      dateRange: appliedDateRange,
    });
    onFilterChange(buildFilterPayload(selections, appliedDateRange));
    setFilterPopoverOpen(false);
  };

  // Handle multi-select for filter options
  const handleOptionSelect = (category: string, value: string) => {
    if (lockMerchantFilter && category === "merchant") return;
    setPendingFilterSelections((prev: any) => ({
      ...prev,
      [category]: prev[category] === value ? "" : value,
    }));
  };

  // Handle search input for filter options
  const handleSearchInput = (category: string, value: string) => {
    setSearchInputs((prev: any) => ({ ...prev, [category]: value }));
  };

  // Clear a single filter category in UI + payload immediately
  const handleClearCategory = (category: string) => {
    if (lockMerchantFilter && category === "merchant") return;

    let nextDateRange = dateRange && dateRange !== "none" ? dateRange : "none";
    const nextSelections = { ...filterSelections };
    const nextPending = { ...pendingFilterSelections };

    if (category === "date") {
      nextDateRange = "none";
      nextSelections.dateRange = "none";
      nextPending.dateRange = "none";
      setDateRange("none");
    } else {
      nextSelections[category] = "";
      nextPending[category] = "";
    }

    setFilterSelections(nextSelections);
    setPendingFilterSelections(nextPending);

    if (category === "merchant") {
      setSearchInputs((prev: any) => ({ ...prev, merchant: "" }));
    }

    onFilterChange(buildFilterPayload(nextSelections, nextDateRange));
  };

  // Clear all filters
  const handleClearAll = () => {
    const lockedMerchant = lockMerchantFilter ? defaultMerchantCode || "" : "";
    setDateRange("none");
    setFilterSelections({
      status: "",
      merchant: lockedMerchant,
      orderStatus: "",
      dateRange: "none",
    });
    setPendingFilterSelections({
      status: "",
      merchant: lockedMerchant,
      orderStatus: "",
      dateRange: "none",
    });
    setSearchInputs({ status: "", merchant: "" });
    onFilterChange({
      status: "",
      orderStatus: "",
      merchantCode: lockedMerchant,
      startDateInMillis: "",
      endDateInMillis: "",
    });
  };

  // Clear current filter
  const handleClearCurrent = () => {
    handleClearCategory(activeCategory);
  };

  // Reset all filters and UI state
  const handleResetAll = () => {
    const lockedMerchant = lockMerchantFilter ? defaultMerchantCode || "" : "";
    setDateRange("");
    setSearchType("memberId");
    onSearch("");
    setFilterSelections({
      status: "",
      merchant: lockedMerchant,
      orderStatus: "",
      dateRange: "none",
    });
    setPendingFilterSelections({
      status: "",
      merchant: lockedMerchant,
      orderStatus: "",
      dateRange: "none",
    });
    setSearchInputs({ status: "", merchant: "" });
    onReset();
  };

  // Render options for the active category
  const renderOptions = () => {
    switch (activeCategory) {
      case "status":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {STATUS_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="status-filter"
                  checked={pendingFilterSelections.status === opt.value}
                  onChange={() => handleOptionSelect("status", opt.value)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">{opt.label}</span>
              </label>
            ))}
            {pendingFilterSelections.status && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleClearCategory("status")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "orderStatus":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {ORDER_STATUS_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="order-status-filter"
                  checked={pendingFilterSelections.orderStatus === opt.value}
                  onChange={() => handleOptionSelect("orderStatus", opt.value)}
                  className="form-radio h-4 w-4 text-blue-600"
                />
                <span className="text-xs font-medium">{opt.label}</span>
              </label>
            ))}
            {pendingFilterSelections.orderStatus && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleClearCategory("orderStatus")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "merchant":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {merchants
              .filter((m: any) =>
                m.merchantName
                  .toLowerCase()
                  .includes((searchInputs.merchant || "").toLowerCase())
              )
              .map((merchant: any) => (
                <label
                  key={merchant.merchantCode}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="merchant-filter"
                    checked={
                      pendingFilterSelections.merchant === merchant.merchantCode
                    }
                    onChange={() =>
                      handleOptionSelect("merchant", merchant.merchantCode)
                    }
                    className="form-radio h-4 w-4 text-blue-600"
                  />
                  <span className="text-xs font-medium">
                    {merchant.merchantName}
                  </span>
                </label>
              ))}
            {pendingFilterSelections.merchant && !lockMerchantFilter && (
              <button
                className="text-xs text-gray-500 hover:underline mt-2 self-start"
                onClick={() => handleClearCategory("merchant")}
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        );
      case "date":
        return (
          <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
            {/* Remove search input for date */}
            <Select
              onValueChange={(v) => {
                setDateRange(v);
                setPendingFilterSelections((prev: any) => ({
                  ...prev,
                  dateRange: v,
                }));
                // Don't call onDateRangeChange immediately - wait for Apply button
              }}
              value={dateRange}
            >
              <SelectTrigger className="h-10 w-full">
                <SelectValue placeholder="Choose Date" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Choose Date</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="7days">Last 7 Days</SelectItem>
                <SelectItem value="30days">Last 30 Days</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Card className="mb-6 border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Visual indicator for locked merchant filter (Merchant Admin) */}
          {lockMerchantFilter && defaultMerchantCode && (
            <div className="mb-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                <Filter className="h-3 w-3" />
                Merchant filter applied:{" "}
                {(() => {
                  const found = (merchants || []).find(
                    (m: any) => m.merchantCode === defaultMerchantCode
                  );
                  return found
                    ? found.merchantName
                    : defaultMerchantCode;
                })()}
              </span>
            </div>
          )}
          {/* Toolbar: Date, Filter Button (search removed) */}
          <div className="flex flex-wrap gap-3 items-center justify-between mb-2">
            <div className="flex gap-3 items-center w-1/2">
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="h-10 w-56">
                  <SelectValue placeholder="Member ID" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merchantTransactionId">
                    Merchant Txn Id
                  </SelectItem>
                  <SelectItem value="loyyalTxnId">Loyyal Txn Id</SelectItem>
                  <SelectItem value="memberId">Member ID</SelectItem>
                </SelectContent>
              </Select>
              <Input
                className="w-64 h-10"
                type="text"
                placeholder={
                  searchType === "merchantTransactionId"
                    ? "Search by Merchant Txn Id"
                    : searchType === "loyyalTxnId"
                    ? "Search by Loyyal Txn Id"
                    : "Search by Member ID"
                }
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
              />
              <div className="w-64 h-10 flex items-center">
                <DatePickerWithRange
                  date={customDateRange}
                  setDate={onCustomDateRangeChange}
                  placeholder="Custom Date Range"
                />
              </div>
            </div>
            <div className="flex gap-2 items-center ml-auto">
              <Popover
                open={filterPopoverOpen}
                onOpenChange={setFilterPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant={activeFilterCount > 0 ? "default" : "outline"}
                    className="flex items-center gap-2 px-3 py-2 text-sm h-10"
                    onClick={() => setFilterPopoverOpen(true)}
                    size="sm"
                  >
                    <Filter className="h-4 w-4" />
                    Filter
                    {activeFilterCount > 0 && (
                      <span className="ml-1 bg-blue-600 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                        {activeFilterCount}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="end">
                  <div className="flex max-h-[320px] h-[320px]">
                    {/* Left: Categories */}
                    <div className="w-32 border-r bg-gray-50 p-2 flex flex-col gap-1">
                      {FILTER_CATEGORIES.map((cat) => (
                        <button
                          key={cat.value}
                          className={`text-left px-2 py-1 rounded hover:bg-gray-100 font-medium text-xs ${
                            activeCategory === cat.value
                              ? "bg-white shadow text-blue-600"
                              : "text-gray-700"
                          }`}
                          onClick={() => setActiveCategory(cat.value)}
                        >
                          {cat.label}
                          {cat.isArray &&
                          Array.isArray(filterSelections[cat.value]) &&
                          filterSelections[cat.value].length > 0 ? (
                            <span className="ml-2 bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 text-[10px] font-semibold">
                              {filterSelections[cat.value].length}
                            </span>
                          ) : null}
                        </button>
                      ))}
                      <div className="flex-1" />
                      <button
                        className="text-[10px] text-gray-500 hover:underline mt-2"
                        onClick={handleClearAll}
                        type="button"
                      >
                        Clear all
                      </button>
                    </div>
                    {/* Right: Options for selected category */}
                    <div className="flex-1 p-2 flex flex-col min-h-0">
                      <div className="flex items-center mb-1">
                        {activeCategory !== "status" &&
                          activeCategory !== "date" && (
                            <Input
                              placeholder={`Search ${activeCategory}`}
                              value={searchInputs[activeCategory] || ""}
                              onChange={(e) =>
                                handleSearchInput(
                                  activeCategory,
                                  e.target.value
                                )
                              }
                              className="h-7 w-full text-xs"
                            />
                          )}
                        {activeCategory !== "status" &&
                          activeCategory !== "date" &&
                          Array.isArray(filterSelections[activeCategory]) &&
                          filterSelections[activeCategory].length > 0 && (
                            <button
                              className="ml-2 text-[10px] text-gray-500 hover:underline"
                              onClick={handleClearCurrent}
                              type="button"
                            >
                              Clear
                            </button>
                          )}
                      </div>
                      <div className="text-xs font-medium flex-1 min-h-0 overflow-y-auto gap-1 flex flex-col">
                        {renderOptions()}
                      </div>
                      <div className="flex justify-end mt-2">
                        <Button
                          size="sm"
                          className="text-xs px-3 py-1 h-7"
                          onClick={applyFilters}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-2 px-3 py-2 text-sm h-10"
                onClick={handleResetAll}
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 px-3 py-2 text-sm h-10"
                  >
                    <Download className="h-4 w-4" />
                    Export
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="end">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="justify-start text-sm"
                      onClick={() => onExport("csv")}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      <span>CSV</span>
                    </Button>
                    {!programAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm"
                        onClick={() => onExport("csv-all-programs")}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        <span>CSV (All Programs)</span>
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
